var _react_refresh_temp_1;
var _react_refresh_temp_2;
_react_refresh_temp_2 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { ENTER_KEY } from '#constants';
import { useVirtualization } from '#hooks';
import { C } from '#integrations';
import { Box } from '../Box';
import { ArrowNarrowDownIcon, ArrowNarrowUpIcon, ChevronSelectorVerticalIcon, } from '../icons';
import { CheckboxInput } from '../inputs/Checkbox';
import { LoadingBox } from '../LoadingBox';
import { Typography } from '../Typography';
import { Pager, TableRoot, TBody, Td, Th, THead, Tr } from './components';
import { useTable } from './context';
import { HEADER_ROW_INDEX, useArrowKeysNavigation, useKeepPageInRange, } from './hooks';
import { TableTestIdFactory } from './interactions';
import { TABLE_SELECT_ALL_VALUE } from './Table.constants';
import { getColumnClassName, sortMultiple } from './Table.services';
import styles from './Table.module.css';
export const Table = ({ className, columnConfigList, data, 'data-testid': testId, dataIdAccessor, emptyStateLabel, focusedRowRefCondition, hasHeader = true, isFullHeight, isLoading, isScrollable, isSelectable, isVirtual, loadingLabel, onRowClick, onSortChange, pagination, rowClassName, rowTestIdAccessor, selectionLabels, }) => {
    _react_refresh_temp_2();
    const { deselect, deselectAll, isSelectAll, select, selectAll, selected, sortConfig, toggleSortOrder, } = useTable();
    const rowIdPrefix = useId();
    const tableRef = useRef(null);
    const tBodyRef = useRef(null);
    const [tHeadClassName, setTHeadClassName] = useState('');
    /**
     * We need to listen whether the TBody has or does not have a scroll
     * so that we can force the scroll of the THead to be present in the WebKit browsers.
     * Otherwise the TBody and the THead elements will be miss-aligned.
     * That scroll won't be visible as it is transparent.
     */
    const onScrollYChange = useCallback((hasScroll) => {
        if (hasScroll && styles.THeadVerticalScrollbar) {
            setTHeadClassName(styles.THeadVerticalScrollbar);
        }
        else {
            setTHeadClassName('');
        }
    }, []);
    const sortFunctions = useMemo(() => {
        if (onSortChange) {
            onSortChange(sortConfig);
            return [];
        }
        return sortConfig
            .map(({ columnId, sortOrder }) => {
            const columnConfig = columnConfigList.find((c) => c.id === columnId);
            if (!columnConfig || !sortOrder) {
                return undefined;
            }
            else {
                const sortingFunction = columnConfig.sortConfig &&
                    columnConfig.sortConfig !== 'external'
                    ? columnConfig.sortConfig[sortOrder]
                    : undefined;
                return sortingFunction;
            }
        })
            .filter(Boolean);
    }, [columnConfigList, onSortChange, sortConfig]);
    const sortedData = useMemo(() => {
        const currentData = [...data];
        sortMultiple(currentData, sortFunctions);
        return currentData;
    }, [data, sortFunctions]);
    const hasData = Boolean(sortedData.length);
    const { hasPager, isPageOutOfRange } = useKeepPageInRange(pagination, isLoading);
    const isLoadingRows = Boolean(isLoading) || isPageOutOfRange;
    const primaryColumnId = columnConfigList.find((columnConfig) => !columnConfig.isHidden)?.id;
    // When selectable, the leading checkbox occupies cell 0, so data columns
    // are shifted one place to the right in the arrow-navigation index.
    const selectionColumnOffset = isSelectable ? 1 : 0;
    // The header row joins the arrow-key navigation only when it is rendered
    // and holds at least one interactive (sortable) column header.
    const isHeaderNavigable = hasHeader &&
        columnConfigList.some((columnConfig) => !columnConfig.isHidden && Boolean(columnConfig.sortConfig));
    const focusedRowIndex = useMemo(() => focusedRowRefCondition
        ? sortedData.findIndex(focusedRowRefCondition)
        : -1, [focusedRowRefCondition, sortedData]);
    const { itemHeight, itemRef, numberOfItems, onContainerScroll, slicedData, startIndex, } = useVirtualization({
        containerRef: tBodyRef,
        isVirtual: Boolean(isVirtual),
        data: sortedData,
        scrollToIndex: focusedRowIndex,
    });
    const { focusableCell, focusableRow, handleBlur, handleKey, shouldFocusRow, } = useArrowKeysNavigation({
        cellsCount: columnConfigList.length + selectionColumnOffset,
        containerRef: tableRef,
        data: sortedData,
        focusedRowRefCondition,
        isHeaderNavigable,
    });
    return (_jsxs("div", { className: C(styles.Container, className, isFullHeight && styles.FullHeight, isScrollable === false && styles.NotScrollable), children: [_jsx(Box, { gap: "l", grow: 1, isFullHeight: isScrollable !== false, isFullWidth: true, scrollableY: hasPager, shrink: 1, children: _jsxs(TableRoot, { className: styles.Table, "data-testid": testId, elementRef: tableRef, onBlur: handleBlur, onKeyDown: handleKey, tableId: testId, ...(numberOfItems && {
                        'aria-rowcount': numberOfItems + 1,
                    }), children: [hasHeader && (_jsx(THead, { className: tHeadClassName, children: _jsxs(Tr, { "data-testid": `head-${testId}`, isHeadRow: true, ...(isVirtual && { 'aria-rowindex': 1 }), children: [isSelectable && (_jsx(Th, { ariaLabel: selectionLabels.column, "data-testid": TableTestIdFactory.getTableSelectAllTestId(testId), isFocusable: focusableRow === HEADER_ROW_INDEX &&
                                            focusableCell === 0, children: _jsx(CheckboxInput, { "aria-label": selectionLabels.selectAll, "data-testid": TableTestIdFactory.getTableSelectAllTestId(testId), id: `table-select-all-${testId}`, isChecked: isSelectAll, kind: "compact", label: "", name: `table-select-all-${testId}`, onChange: () => {
                                                if (isSelectAll) {
                                                    deselectAll();
                                                }
                                                else {
                                                    selectAll(sortedData.map((dataItem) => dataIdAccessor(dataItem)));
                                                }
                                            }, value: TABLE_SELECT_ALL_VALUE }) })), columnConfigList.map((columnConfig, columnIndex) => {
                                        const hasSorting = Boolean(columnConfig.sortConfig);
                                        const existingSortConfig = sortConfig.find((sortConfigItem) => sortConfigItem.columnId ===
                                            columnConfig.id);
                                        // Mirror the data cell index so the
                                        // header cell lines up vertically with
                                        // the same column in the first row.
                                        const numberOfHiddenColumns = columnConfigList
                                            .slice(0, columnIndex)
                                            .filter((config) => config.isHidden).length;
                                        return !columnConfig.isHidden ? (_jsxs(Th, { ariaLabel: columnConfig.ariaLabel, ariaSort: hasSorting
                                                ? (existingSortConfig?.sortOrder ??
                                                    'none')
                                                : undefined, className: getColumnClassName(columnConfig), "data-testid": TableTestIdFactory.internalGenerateTableHeadTestId(columnConfig.id, testId), isFocusable: focusableRow ===
                                                HEADER_ROW_INDEX &&
                                                focusableCell ===
                                                    selectionColumnOffset +
                                                        columnIndex +
                                                        numberOfHiddenColumns, ...(hasSorting
                                                ? {
                                                    onClick: () => {
                                                        toggleSortOrder({
                                                            columnId: columnConfig.id,
                                                        });
                                                    },
                                                }
                                                : {}), children: [_jsx(Typography, { "data-testid": "", kind: "subtitle", children: columnConfig.label }), hasSorting && (_jsx("div", { children: existingSortConfig?.sortOrder ? (existingSortConfig?.sortOrder ===
                                                        'ascending' ? (_jsx(ArrowNarrowDownIcon, { size: "small" })) : (_jsx(ArrowNarrowUpIcon, { size: "small" }))) : (_jsx(ChevronSelectorVerticalIcon, { size: "small" })) }))] }, columnConfig.id)) : null;
                                    })] }) })), _jsxs(TBody, { "data-testid": testId, isScrollable: isScrollable, onScrollYChange: onScrollYChange, onTBodyScroll: onContainerScroll, tBodyRef: tBodyRef, children: [(slicedData || sortedData).map((dataItem, index) => {
                                    const stableStartIndex = Math.max(startIndex || 0, 0);
                                    const id = dataIdAccessor(dataItem);
                                    const currentAbsoluteIndex = stableStartIndex + index;
                                    const testIdSegment = rowTestIdAccessor
                                        ? rowTestIdAccessor(dataItem, currentAbsoluteIndex)
                                        : id;
                                    const primaryCellId = `${rowIdPrefix}row-${currentAbsoluteIndex}-primary`;
                                    const selectCellId = `${rowIdPrefix}row-${currentAbsoluteIndex}-select`;
                                    const isOdd = (index + stableStartIndex + 1) % 2 !== 0;
                                    const isFirstVisible = isVirtual && index === 0;
                                    const isLastVisible = isVirtual &&
                                        slicedData &&
                                        index === slicedData.length - 1;
                                    let numberOfHiddenColumns = 0;
                                    return (_jsxs(Tr, { className: rowClassName, "data-testid": TableTestIdFactory.internalGenerateTableRowTestId(testIdSegment, testId), elementRef: index === 1 ? itemRef : undefined, isFocusable: focusableRow === currentAbsoluteIndex &&
                                            focusableCell === null, isOdd: isOdd, isSelected: selected.includes(id), onClick: (event) => {
                                            if (onRowClick) {
                                                onRowClick(event, dataItem);
                                            }
                                        }, shouldFocusRow: shouldFocusRow, ...(isFirstVisible &&
                                            itemHeight && {
                                            virtualMarginTop: stableStartIndex * itemHeight,
                                        }), ...(isLastVisible && {
                                            virtualMarginBottom: (numberOfItems -
                                                (stableStartIndex + index) -
                                                1) *
                                                itemHeight,
                                        }), ...(isVirtual && {
                                            'aria-rowindex': 
                                            // The index of the row is calculated:
                                            // * the index of the item in the current virtual set +
                                            // * the index of the first item in the virtual set within the entire data set +
                                            // * 2 - which is offset as the rowindex starts from 1 and includes the header row
                                            index + stableStartIndex + 2,
                                        }), children: [isSelectable && (_jsx(Td, { "data-testid": TableTestIdFactory.getTableSelectCellTestId(testIdSegment, testId), isFocusable: focusableRow ===
                                                    currentAbsoluteIndex &&
                                                    focusableCell === 0, onClick: (event) => {
                                                    event.stopPropagation();
                                                }, onFocus: (event) => {
                                                    event.stopPropagation();
                                                }, onKeyDown: (event) => {
                                                    // Isolate Enter from the row, but
                                                    // let arrow keys reach the table's
                                                    // navigation handler.
                                                    if (event.key === ENTER_KEY) {
                                                        event.stopPropagation();
                                                    }
                                                }, children: _jsx(CheckboxInput, { "aria-labelledby": primaryCellId, "data-testid": TableTestIdFactory.getTableSelectCellTestId(testIdSegment, testId), id: selectCellId, isChecked: selected.includes(id), kind: "compact", label: "", name: selectCellId, onChange: () => {
                                                        if (selected.includes(id)) {
                                                            deselect(id);
                                                        }
                                                        else {
                                                            select(id);
                                                        }
                                                    }, value: testIdSegment }) })), columnConfigList.map((columnConfig, cellIndex) => {
                                                const isTooltipEnabled = columnConfig.tooltip &&
                                                    columnConfig.tooltip.enabled
                                                    ? columnConfig.tooltip.enabled(dataItem)
                                                    : true;
                                                if (columnConfig.isHidden) {
                                                    numberOfHiddenColumns++;
                                                }
                                                return !columnConfig.isHidden ? (_jsx(Td, { className: C(getColumnClassName(columnConfig), columnConfig.stopPropagation &&
                                                        styles.NoPropagate), "data-testid": TableTestIdFactory.internalGenerateTableCellTestId(testIdSegment, columnConfig.id, testId), id: columnConfig.id ===
                                                        primaryColumnId
                                                        ? primaryCellId
                                                        : undefined, isFocusable: focusableRow ===
                                                        currentAbsoluteIndex &&
                                                        focusableCell ===
                                                            selectionColumnOffset +
                                                                cellIndex +
                                                                numberOfHiddenColumns, onClick: (event) => {
                                                        if (columnConfig.stopPropagation) {
                                                            event.stopPropagation();
                                                        }
                                                    }, onFocus: (event) => {
                                                        if (columnConfig.stopPropagation) {
                                                            event.stopPropagation();
                                                        }
                                                    }, onKeyDown: (event) => {
                                                        if (columnConfig.stopPropagation &&
                                                            event.key ===
                                                                ENTER_KEY) {
                                                            event.stopPropagation();
                                                        }
                                                    }, ...(columnConfig.tooltip &&
                                                        isTooltipEnabled && {
                                                        tooltip: {
                                                            content: typeof columnConfig
                                                                .tooltip
                                                                .content ===
                                                                'function'
                                                                ? columnConfig.tooltip.content(dataItem)
                                                                : columnConfig
                                                                    .tooltip
                                                                    .content,
                                                            placement: columnConfig
                                                                .tooltip
                                                                .placement,
                                                        },
                                                    }), children: columnConfig.renderData(dataItem) }, columnConfig.id)) : null;
                                            })] }, id));
                                }), !hasData &&
                                    !isLoadingRows &&
                                    Boolean(emptyStateLabel) && (_jsx("tr", { children: _jsx("td", { className: styles.EmptyState, colSpan: columnConfigList.length, "data-testid": TableTestIdFactory.getTableEmptyTestId(testId), children: emptyStateLabel }) })), isLoadingRows && !hasData && (_jsx("tr", { className: styles.Loading, children: _jsx("td", { className: styles.Loading, colSpan: columnConfigList.length, children: _jsx(LoadingBox, { alt: loadingLabel, "data-testid": TableTestIdFactory.getTableLoadingTestId(testId) }) }) }))] })] }) }), pagination && hasPager && (_jsx(Pager, { tableId: testId, ...pagination }))] }));
};
_react_refresh_temp_1 = Table;
$RefreshReg$(_react_refresh_temp_1, "Table");
_react_refresh_temp_2(Table, "79iiwVeS3P6zZT4tLTY97NFIcYk=", false, () => [useTable, useKeepPageInRange, useVirtualization, useArrowKeysNavigation]);
